import trashImage from 'images/formbuilder/trash.svg';
import * as React from 'react';
import Lightbox from 'react-image-lightbox';
import { getTranslate, Translate } from 'react-localize-redux';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownTags } from '../../components_v2/dropdown/DropdownTagsCloud';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import Input from '../../components_v2/input/Input';
import InputImage from '../../components_v2/input/InputImage';
import { ImageThumbnail } from '../../components_v2/input/model/Model';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import PopupCreation from '../../components_v2/popup/PopupCreation';
import storeLang from '../../helpers/storeLang';
import { ITag } from '../../../typings/proto/protobufs';
import { CreationPopupContainer, creationPopupDropdownStyle, creationPopupInputStyle, Label, TitleSection } from '../../components_v2/popup/style/CreationFormStyle';
import { normalizeInt, normalizePrice } from '../globals/FieldFunction';
import { Brand } from '../globals/Model';
import { getCurrencySymbol } from '../reports/utils';
import { getTaxes } from '../settings/actions';
import { Tax } from '../settings/models';
import { getBrands, getTags, postProduct, postImages, updateProduct } from './action';
import LegacyCategoryModal from './legacyCategoryModal';
import { ProductsAlternativeList } from './productAlternativesCreation';
import { Content, FlexDiv, HideTrashDiv, Picture, PictureTrash, SymbolContainer, TopRight } from './style';
import { translateToNode, translateToString } from '../../styles/global/translate';
import useAlert from '../alert/UseAlert';
import { parseNumber } from '../../components_v2/utils';

interface ProductCreationType {
  name?: string
  images?: ImageThumbnail[]
  reference?: string
  category?: [number, string]
  stock?: number
  code_upc?: string
  barcode?: string
  price?: string
  recommended_price?: string
  tax?: number
  description?: string
  resume?: string
  brand?: number
  tags?: number[]
  alternatives?: number[]
  outer?: number
  status?: number
  additional_taxes?: Tax[],
  width?: number,
  height?: number,
  depth?: number
}

export default function ProductCreation(props: {
  isOpen: boolean
  setIsOpen: (b: boolean) => void
  refresh?: () => void
}): JSX.Element {
	const { isOpen, setIsOpen, refresh } = props;

	const [brands, setBrands] = React.useState<Brand[]>([]);
	const [tags, setTags] = React.useState<ITag[]>([]);

	const [current, setCurrent] = React.useState<number>(0);
	const [isLightboxOpen, setIsLightboxOpen] = React.useState<boolean>(false);
	const [isCategoryOpen, setIsCategoryOpen] = React.useState<boolean>(false);
	const [isAlternativeOpen, setIsAlternativeOpen] = React.useState<boolean>(false);
	const [creation, setCreation] = React.useState<ProductCreationType>({});
	const translate = getTranslate(storeLang.getState().localize);
	const [taxes, setTaxes] = React.useState<Tax[]>([]);
	const alert = useAlert();

	React.useEffect(() => {
		getBrands()
			.then(res => setBrands(res.data))
			.catch(console.log);
		getTags().then(tags => {
			setTags(tags);
		})
			.catch(console.log);
		getTaxes().then((taxes: Tax[]) => {
			setTaxes(taxes);
			setCreation({ ...creation, additional_taxes: taxes });
		}).catch(console.log);
	}, []);

	function transformAndCreate() {
		const body = {
			...creation,
			price: creation.price ? parseFloat(creation.price) : undefined,
			recommended_price: creation.recommended_price ? parseFloat(creation.recommended_price) : undefined,
			category: creation.category?.[0],
			images: creation.images?.map(image => ({ name: 'thumbs/' + image.thumbnail.name, file: '' })),
			additional_taxes: creation.additional_taxes?.map(t => ({ id: t.id, value: t.tax_value }))
		};
		if (creation.images) {
			creation.images?.map(async(image) => {await postImages([image.thumbnail, image.original], ['thumbs/' + image.thumbnail.name, 'originals/' + image.original.name]);});
		}
		postProduct(body)
			.then((res) => {
				const productId = res.data;
				const dimensions = { width: creation.width, height: creation.height, depth: creation.depth };
				const price = creation.price ? parseFloat(creation.price) : undefined;
				const recommended_price = creation.recommended_price ? parseFloat(creation.recommended_price) : undefined;
				const prices = { price, recommended_price };
				updateProduct(productId, { ...dimensions, ...prices });
				setCreation({});
				setCurrent(0);
				setIsOpen(false);
				refresh?.();
			});
	}

	const isValid: boolean = ((creation.name?.trim() ?? '') !== '') &&
		((creation.reference?.trim() ?? '') !== '') &&
		(creation.category?.[0] !== undefined) &&
		(creation.barcode !== undefined) &&
		(creation.code_upc !== undefined) &&
		(creation.price == undefined || creation.price == '' || !isNaN(parseFloat(creation.price))) &&
		(creation.recommended_price == undefined || creation.recommended_price == '' || !isNaN(parseFloat(creation.recommended_price)));

	return (
		<Popup
			isOpen={isOpen}
			onClickOut={() => setIsOpen(false)}
			popupStyle={{ animate: true, height: '100vh', top: '0' }}
			popupMode={PopupMode.Details}
			disableOutClick={isCategoryOpen || isLightboxOpen || isAlternativeOpen}
			disableEsc={isCategoryOpen || isLightboxOpen || isAlternativeOpen}
		>
			<PopupCreation
				title={<Translate id='create_product' />}
				canValidate={isValid}
				onSubmit={transformAndCreate}
				onClose={() => setIsOpen(false)}
			>
				<CreationPopupContainer>
					<TitleSection><Translate id='Product details' /></TitleSection>
					<Label><Translate id='Picture' /></Label>
					<Content row gap={10} left wrap>
						{creation.images?.map((v, i) => <>
							<HideTrashDiv>
								<Picture src={v.url} onClick={() => { setCurrent(i); setIsLightboxOpen(true); }} />
								<TopRight>
									<PictureTrash className='trash' src={trashImage} onClick={() => {
										setCreation(prevCreation => ({
											...prevCreation,
											images: prevCreation.images?.filter((_, index) => index !== i)
										}));
									}} />
								</TopRight>
							</HideTrashDiv>
						</>)}
						{(creation.images?.length ?? 0) < 5 &&
							<InputImage
								isProduct={true}
								compress={true}
								limitMo={10}
								disableShowImage
								onChange={(value) => {
									setCreation(prevCreation => ({ ...prevCreation, images: [...(prevCreation.images ?? []), value] }));
								}}
							/>
						}
						{isLightboxOpen &&
							<Lightbox
								mainSrc={creation.images?.[current]?.url ?? ''}
								nextSrc={creation.images?.[current + 1]?.url}
								prevSrc={creation.images?.[current - 1]?.url}
								onCloseRequest={() => setIsLightboxOpen(false)}
								onMovePrevRequest={() => setCurrent(current - 1)}
								onMoveNextRequest={() => setCurrent(current + 1)}
							/>
						}
					</Content>
					<Label required><Translate id='name' /></Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_name"
						type="text"
						placeholder={translate('name') as string}
						required
						value={creation.name}
						onChange={(value) => setCreation({ ...creation, name: value })}
					/>
					<Label required>{translate('reference') as string}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_reference"
						type="text"
						placeholder={translate('reference') as string}
						required
						value={creation.reference}
						onChange={(value) => setCreation({ ...creation, reference: value })}
					/>
					<Label required>{translate('category') as string}</Label>
					<Input
						onClick={() => setIsCategoryOpen(true)}
						inputStyle={creationPopupInputStyle}
						name="product_category"
						type="text"
						placeholder={translate('category') as string}
						required
						value={creation.category?.[1]}
						disableChange
					/>
					<LegacyCategoryModal
						isOpen={isCategoryOpen}
						toggle={() => setIsCategoryOpen(!isCategoryOpen)}
						onSubmit={(id, name) => id && name && setCreation({ ...creation, category: [id, name] })}
					/>
					<Label required>{translate('barcode') as string}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_barcode"
						type="text"
						placeholder={translate('barcode') as string}
						value={creation.barcode}
						required
						onChange={(barcode) => setCreation({ ...creation, barcode })}
					/>
					<Label required>{translate('UPC Barcode') as string}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_code_UPC"
						type="text"
						placeholder={translate('UPC Barcode') as string}
						value={creation.code_upc}
						required
						onChange={(code_upc) => setCreation({ ...creation, code_upc })}
					/>
					<Label>{translate('brand') as string}</Label>
					<Dropdown
						dropdownStyle={creationPopupDropdownStyle}
						datalist={brands.map(b => ({ label: b.name, value: b.id }))}
						name='brand_dropdown'
						readOnly
						onChange={(brand: DropdownData) => {
							if (brand.value == null) {
								setCreation({ ...creation, brand: undefined });
							} else {
								setCreation({ ...creation, brand: brand.value });
							}
						}}
						cancellable
					/>
					<Label>{translate('tags') as string}</Label>
					<DropdownTags
						dropdownStyle={creationPopupDropdownStyle}
						tags={tags}
						selected={tags.filter(t => creation.tags?.includes(t.id!))}
						onChange={(values) => setCreation({ ...creation, tags: values.map(e => e.value.id!) })}
					/>
					<Label>{translate('stock') as string}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_stocks"
						type="text"
						placeholder={translate('stock') as string}
						value={creation.stock}
						normalize={[normalizeInt]}
						onChange={(value) => {
							let stock: number | undefined = parseInt(value);
							if (isNaN(stock)) {
								stock = undefined;
							}
							setCreation({ ...creation, stock });
						}}
					/>
					<Label>{translateToString('width') + ' (cm)'}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_width"
						type="number"
						step={0.1}
						placeholder={translateToString('width') + ' (cm)'}
						value={creation.width}
						normalize={[normalizePrice]}
						onChange={(value) => {
							let width: number | undefined = parseNumber(value);
							if (isNaN(width)) {
								width = undefined;
							}
							setCreation({ ...creation, width });
						}}
					/>
					<Label>{translateToString('height') + ' (cm)'}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_height"
						type="number"
						step={0.1}
						placeholder={translateToString('height') + ' (cm)'}
						value={creation.height}
						normalize={[normalizePrice]}
						onChange={(value) => {
							let height: number | undefined = parseNumber(value);
							if (isNaN(height)) {
								height = undefined;
							}
							setCreation({ ...creation, height });
						}}
					/>
					<Label>{translateToString('depth') + ' (cm)'}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_depth"
						type="number"
						step={0.1}
						placeholder={translateToString('depth') + ' (cm)'}
						value={creation.depth}
						normalize={[normalizePrice]}
						onChange={(value) => {
							let depth: number | undefined = parseNumber(value);
							if (isNaN(depth)) {
								depth = undefined;
							}
							setCreation({ ...creation, depth });
						}}
					/>
					<TitleSection><Translate id='price' /></TitleSection>
					<Label>{translate('price') as string}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_price"
						type="text"
						placeholder={translate('price') as string}
						value={creation.price}
						normalize={[normalizePrice]}
						onChange={(value) => setCreation({ ...creation, price: value })}
					/>
					<Label>{translate('recommended price') as string}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_recommendedPrice"
						type="text"
						placeholder={translate('recommended price') as string}
						value={creation.recommended_price}
						normalize={[normalizePrice]}
						onChange={(value) => setCreation({ ...creation, recommended_price: value })}
					/>
					{!taxes.length && <>
						<Label>{translate('tax') as string}</Label>
						<Input
							inputStyle={creationPopupInputStyle}
							name="product_tax"
							type="text"
							disableChange
							placeholder={translate('tax') as string}
							onClick={() => {
								if (taxes.length === 0) {
									alert({
										title: translateToNode('taxes'),
										content: <div>{translateToNode('no_data_yet', { onClickA: () => window.open('/settings#tax_management', '_blank'), variables: [['data', translateToString('taxes').toLocaleLowerCase()]] })}</div>,
										noButtons: true
									});
								}
							}}
						/>
					</>}
					{taxes.map((t, i) => {
						let symbol: string;
						switch (t.tax_type_name) {
							case 'percentage':
								symbol = '%';
								break;
							case 'amount':
								symbol = getCurrencySymbol();
								break;
							default:
								symbol = t.tax_type_name;
								break;
						}
						return <React.Fragment key={`tax[${t.id}]`}>
							<Label>{t.name}</Label>
							<FlexDiv gap='2px'>
								<Input
									inputStyle={{ ...creationPopupInputStyle, width: '213px' }}
									name={`product_tax[${i}]`}
									type="text"
									placeholder={t.name}
									value={creation.additional_taxes?.[i]?.tax_value}
									normalize={[normalizeInt]}
									onChange={(value) => {
										let tax: number | undefined = parseInt(value);
										if (isNaN(tax)) {
											tax = undefined;
										}
										if (creation.additional_taxes != null) {
											creation.additional_taxes[i].tax_value = tax;
										}
										setCreation({ ...creation });
									}}
								/>
								{/* @ts-expect-error style? */}
								<SymbolContainer style={{ ...creationPopupInputStyle, width: 'fit-content' }}>
									{symbol}
								</SymbolContainer>
							</FlexDiv>
						</React.Fragment>;
					})}
					<Label>{translate('outer') as string}</Label>
					<Input
						inputStyle={creationPopupInputStyle}
						name="product_outer"
						type="text"
						placeholder={translate('outer') as string}
						value={creation.outer}
						normalize={[normalizeInt]}
						onChange={(value) => {
							let outer: number | undefined = parseInt(value);
							if (isNaN(outer)) {
								outer = undefined;
							}
							setCreation({ ...creation, outer });
						}}
					/>
					<TitleSection><Translate id='Caracteristics' /></TitleSection>
					<Label>{translate('summary') as string}</Label>
					<Input
						inputStyle={{ ...creationPopupInputStyle, height: '100px' }}
						name="product_resume"
						type="textarea"
						placeholder={translate('summary') as string}
						value={creation.resume}
						onChange={(value) => setCreation({ ...creation, resume: value })}
						textArea
					/>
					<Label>{translate('description') as string}</Label>
					<Input
						textArea
						inputStyle={{ ...creationPopupInputStyle, height: '100px' }}
						name="product_description"
						type="textarea"
						placeholder={translate('description') as string}
						value={creation.description}
						onChange={(value) => setCreation({ ...creation, description: value })}
					/>
					<TitleSection><Translate id='Alternative products' /></TitleSection>
					<Label>{translate('Alternative products') as string}</Label>
					<Input
						onClick={() => setIsAlternativeOpen(true)}
						inputStyle={creationPopupInputStyle}
						name="product_alternatives"
						type="text"
						placeholder={translate('Alternative products') as string}
						value={translate('{{NUMBER}} selected').toString().replace('{{NUMBER}}', `${creation.alternatives?.length ?? 0}`)}
						disableChange
					/>
					<Popup
						popupStyle={{ height: 'calc(40px * 13)' }}
						isOpen={isAlternativeOpen}
						onClickOut={() => setIsAlternativeOpen(false)}
						hasBackground
					>
						<FlexDiv justify='center'>
							<ProductsAlternativeList
								noTitle
								onChange={(alternatives: number[]) => setCreation({ ...creation, alternatives })}
								productId={-1}
								limit={10}
								tableHeight={'calc(100% - 20px)'}
							/>
						</FlexDiv>
					</Popup>
				</CreationPopupContainer>
			</PopupCreation>
		</Popup>
	);
}
